import {
  type BusinessMetricDictionaryReadModel,
  type BusinessMetricReadModel,
  type BusinessMetricReportBundleReadModel,
  type BusinessMetricReportReadModel,
  type CashDifferenceBusinessMetricDetailReadModel,
  type CommissionPayableBusinessMetricDetailReadModel,
  type ConfirmedReceiptBusinessMetricDetailReadModel,
  isBusinessMetricDictionaryReadModel,
  isBusinessMetricReportBundleReadModel,
  isBusinessMetricReportReadModel,
  isCashDifferenceBusinessMetricDetailReadModel,
  isCommissionPayableBusinessMetricDetailReadModel,
  isConfirmedReceiptBusinessMetricDetailReadModel,
  isMemberFundBusinessMetricDetailReadModel,
  isProductCostBusinessMetricDetailReadModel,
  isRefundBusinessMetricDetailReadModel,
  type MemberFundBusinessMetricDetailReadModel,
  type MemberFundBusinessMetricId,
  type ProductCostBusinessMetricDetailReadModel,
  type RefundBusinessMetricDetailReadModel,
} from "@billiard/contracts";
import { ApiRequestError, requestOperatorJson } from "./api";

export interface BusinessMetricCurrentOperatorContext {
  tenantId: string;
  storeId: string;
}

export interface MemberFundBusinessMetricDetailOperatorContext
  extends BusinessMetricCurrentOperatorContext {
  operatorId: string;
}

export interface RefundBusinessMetricDetailOperatorContext
  extends BusinessMetricCurrentOperatorContext {
  operatorId: string;
}

export interface ConfirmedReceiptBusinessMetricDetailOperatorContext
  extends BusinessMetricCurrentOperatorContext {
  operatorId: string;
}

export interface ProductCostBusinessMetricDetailOperatorContext
  extends BusinessMetricCurrentOperatorContext {
  operatorId: string;
}

export interface CommissionPayableBusinessMetricDetailOperatorContext
  extends BusinessMetricCurrentOperatorContext {
  operatorId: string;
}

export interface CashDifferenceBusinessMetricDetailOperatorContext
  extends BusinessMetricCurrentOperatorContext {
  operatorId: string;
}

export type BusinessMetricDictionaryQueryKey = readonly [
  "business-metric-dictionary",
  string,
  string,
  string,
];

export type BusinessMetricReportQueryKey = readonly [
  "business-metric-report",
  string,
  string,
  string,
];

export type BusinessMetricReportBundleQueryKey = readonly [
  "business-metric-report-bundle",
  string,
  string,
  string,
];

export type MemberFundBusinessMetricDetailQueryKey = readonly [
  "member-fund-business-metric-detail",
  string,
  string,
  string,
  string,
  MemberFundBusinessMetricId,
];

export type RefundBusinessMetricDetailQueryKey = readonly [
  "refund-business-metric-detail",
  string,
  string,
  string,
  string,
];

export type ConfirmedReceiptBusinessMetricDetailQueryKey = readonly [
  "confirmed-receipt-business-metric-detail",
  string,
  string,
  string,
  string,
];

export type ProductCostBusinessMetricDetailQueryKey = readonly [
  "product-cost-business-metric-detail",
  string,
  string,
  string,
  string,
];

export type CommissionPayableBusinessMetricDetailQueryKey = readonly [
  "commission-payable-business-metric-detail",
  string,
  string,
  string,
  string,
];

export type CashDifferenceBusinessMetricDetailQueryKey = readonly [
  "cash-difference-business-metric-detail",
  string,
  string,
  string,
  string,
];

export interface BusinessMetricDisplayModel {
  availabilityLabel: "数据完整" | "部分可用" | "暂不可用";
  valueLabel: string;
  reason: string | null;
  attention: boolean;
}

const drillHrefByTarget: Record<BusinessMetricReadModel["drillTarget"], string> = {
  ORDERS: "/orders",
  RECONCILIATION: "/orders",
  MEMBER_FUNDS: "/members",
  RECEIVABLES: "/review",
  COACH_SERVICES: "/coach",
  INVENTORY: "/products",
  CASH_SHIFTS: "/shifts",
};

function isCanonicalDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) && new Date(milliseconds).toISOString().slice(0, 10) === value
  );
}

function operatorContextIsCanonical(context: BusinessMetricCurrentOperatorContext): boolean {
  return [context.tenantId, context.storeId].every(
    (value) => value.length >= 1 && value.length <= 128 && value.trim() === value,
  );
}

function memberFundMetricIdIsCanonical(value: unknown): value is MemberFundBusinessMetricId {
  return value === "STORED_VALUE_PRINCIPAL" || value === "BONUS_VALUE";
}

function detailOperatorContextIsCanonical(
  context: BusinessMetricCurrentOperatorContext & { operatorId: string },
): boolean {
  return (
    operatorContextIsCanonical(context) &&
    context.operatorId.length >= 1 &&
    context.operatorId.length <= 128 &&
    context.operatorId.trim() === context.operatorId
  );
}

function memberFundBusinessMetricDetailRequestIsCanonical(
  businessDate: string,
  metricId: unknown,
  context: MemberFundBusinessMetricDetailOperatorContext,
): metricId is MemberFundBusinessMetricId {
  return (
    isCanonicalDate(businessDate) &&
    memberFundMetricIdIsCanonical(metricId) &&
    detailOperatorContextIsCanonical(context)
  );
}

function memberFundBusinessMetricDetailRequestInvalid(): ApiRequestError {
  return new ApiRequestError(
    "REQUEST_VALIDATION_FAILED",
    "会员资金明细的营业日期、指标或当前门店身份不正确，系统没有读取明细",
    400,
  );
}

function formatFen(value: number): string {
  return new Intl.NumberFormat("zh-CN", {
    style: "currency",
    currency: "CNY",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);
}

function isMemberBalanceMovementMetric(metric: BusinessMetricReadModel): boolean {
  return metric.id === "STORED_VALUE_PRINCIPAL" || metric.id === "BONUS_VALUE";
}

function formatMemberBalanceMovement(value: number): string {
  if (value > 0) return `净增加 ${formatFen(value)}`;
  if (value < 0) return `净减少 ${formatFen(Math.abs(value))}`;
  return `无净变动 ${formatFen(0)}`;
}

export function businessMetricDictionaryQueryKey(
  currentOperatorContext: BusinessMetricCurrentOperatorContext,
  businessDate: string,
): BusinessMetricDictionaryQueryKey {
  return [
    "business-metric-dictionary",
    currentOperatorContext.tenantId,
    currentOperatorContext.storeId,
    businessDate,
  ];
}

export function businessMetricReportQueryKey(
  currentOperatorContext: BusinessMetricCurrentOperatorContext,
  businessDate: string,
): BusinessMetricReportQueryKey {
  return [
    "business-metric-report",
    currentOperatorContext.tenantId,
    currentOperatorContext.storeId,
    businessDate,
  ];
}

export function businessMetricReportBundleQueryKey(
  currentOperatorContext: BusinessMetricCurrentOperatorContext,
  businessDate: string,
): BusinessMetricReportBundleQueryKey {
  return [
    "business-metric-report-bundle",
    currentOperatorContext.tenantId,
    currentOperatorContext.storeId,
    businessDate,
  ];
}

export function memberFundBusinessMetricDetailQueryKey(
  currentOperatorContext: MemberFundBusinessMetricDetailOperatorContext,
  businessDate: string,
  metricId: MemberFundBusinessMetricId,
): MemberFundBusinessMetricDetailQueryKey {
  if (
    !memberFundBusinessMetricDetailRequestIsCanonical(
      businessDate,
      metricId,
      currentOperatorContext,
    )
  ) {
    throw memberFundBusinessMetricDetailRequestInvalid();
  }
  return [
    "member-fund-business-metric-detail",
    currentOperatorContext.tenantId,
    currentOperatorContext.storeId,
    currentOperatorContext.operatorId,
    businessDate,
    metricId,
  ];
}

export async function getMemberFundBusinessMetricDetail(
  businessDate: string,
  metricId: MemberFundBusinessMetricId,
  currentOperatorContext: MemberFundBusinessMetricDetailOperatorContext,
  signal?: AbortSignal,
): Promise<MemberFundBusinessMetricDetailReadModel> {
  if (
    !memberFundBusinessMetricDetailRequestIsCanonical(
      businessDate,
      metricId,
      currentOperatorContext,
    )
  ) {
    throw memberFundBusinessMetricDetailRequestInvalid();
  }
  const query = new URLSearchParams({ businessDate, metricId });
  const value = await requestOperatorJson<unknown>(
    `/v1/owner/business-metrics/member-fund-details?${query.toString()}`,
    {
      cache: "no-store",
      ...(signal ? { signal } : {}),
    },
  );
  if (
    !isMemberFundBusinessMetricDetailReadModel(value, {
      tenantId: currentOperatorContext.tenantId,
      storeId: currentOperatorContext.storeId,
      businessDate,
      metricId,
    })
  ) {
    throw new ApiRequestError(
      "GATEWAY_RESPONSE_INVALID",
      "会员资金明细无法安全确认，页面已隐藏旧明细和金额，请重新读取",
    );
  }
  return value;
}

function refundBusinessMetricDetailRequestInvalid(): ApiRequestError {
  return new ApiRequestError(
    "REQUEST_VALIDATION_FAILED",
    "退款明细的营业日期或当前操作身份不正确，系统没有读取明细",
    400,
  );
}

export function refundBusinessMetricDetailQueryKey(
  currentOperatorContext: RefundBusinessMetricDetailOperatorContext,
  businessDate: string,
): RefundBusinessMetricDetailQueryKey {
  if (!isCanonicalDate(businessDate) || !detailOperatorContextIsCanonical(currentOperatorContext)) {
    throw refundBusinessMetricDetailRequestInvalid();
  }
  return [
    "refund-business-metric-detail",
    currentOperatorContext.tenantId,
    currentOperatorContext.storeId,
    currentOperatorContext.operatorId,
    businessDate,
  ];
}

export async function getRefundBusinessMetricDetail(
  businessDate: string,
  currentOperatorContext: RefundBusinessMetricDetailOperatorContext,
  signal?: AbortSignal,
): Promise<RefundBusinessMetricDetailReadModel> {
  if (!isCanonicalDate(businessDate) || !detailOperatorContextIsCanonical(currentOperatorContext)) {
    throw refundBusinessMetricDetailRequestInvalid();
  }
  const query = new URLSearchParams({ businessDate });
  const value = await requestOperatorJson<unknown>(
    `/v1/owner/business-metrics/refund-details?${query.toString()}`,
    {
      cache: "no-store",
      ...(signal ? { signal } : {}),
    },
  );
  if (
    !isRefundBusinessMetricDetailReadModel(value, {
      tenantId: currentOperatorContext.tenantId,
      storeId: currentOperatorContext.storeId,
      businessDate,
    })
  ) {
    throw new ApiRequestError(
      "GATEWAY_RESPONSE_INVALID",
      "退款明细无法安全确认，页面已隐藏旧明细和金额，请重新读取",
    );
  }
  return value;
}

function confirmedReceiptBusinessMetricDetailRequestInvalid(): ApiRequestError {
  return new ApiRequestError(
    "REQUEST_VALIDATION_FAILED",
    "已确认实收明细的营业日期或当前操作身份不正确，系统没有读取明细",
    400,
  );
}

export function confirmedReceiptBusinessMetricDetailQueryKey(
  currentOperatorContext: ConfirmedReceiptBusinessMetricDetailOperatorContext,
  businessDate: string,
): ConfirmedReceiptBusinessMetricDetailQueryKey {
  if (!isCanonicalDate(businessDate) || !detailOperatorContextIsCanonical(currentOperatorContext)) {
    throw confirmedReceiptBusinessMetricDetailRequestInvalid();
  }
  return [
    "confirmed-receipt-business-metric-detail",
    currentOperatorContext.tenantId,
    currentOperatorContext.storeId,
    currentOperatorContext.operatorId,
    businessDate,
  ];
}

export async function getConfirmedReceiptBusinessMetricDetail(
  businessDate: string,
  currentOperatorContext: ConfirmedReceiptBusinessMetricDetailOperatorContext,
  signal?: AbortSignal,
): Promise<ConfirmedReceiptBusinessMetricDetailReadModel> {
  if (!isCanonicalDate(businessDate) || !detailOperatorContextIsCanonical(currentOperatorContext)) {
    throw confirmedReceiptBusinessMetricDetailRequestInvalid();
  }
  const query = new URLSearchParams({ businessDate });
  const value = await requestOperatorJson<unknown>(
    `/v1/owner/business-metrics/confirmed-receipt-details?${query.toString()}`,
    {
      cache: "no-store",
      ...(signal ? { signal } : {}),
    },
  );
  if (
    !isConfirmedReceiptBusinessMetricDetailReadModel(value, {
      tenantId: currentOperatorContext.tenantId,
      storeId: currentOperatorContext.storeId,
      businessDate,
    })
  ) {
    throw new ApiRequestError(
      "GATEWAY_RESPONSE_INVALID",
      "已确认实收明细无法安全确认，页面已隐藏旧明细和金额，请重新读取",
    );
  }
  return value;
}

function productCostBusinessMetricDetailRequestInvalid(): ApiRequestError {
  return new ApiRequestError(
    "REQUEST_VALIDATION_FAILED",
    "商品成本明细的营业日期或当前操作身份不正确，系统没有读取明细",
    400,
  );
}

export function productCostBusinessMetricDetailQueryKey(
  currentOperatorContext: ProductCostBusinessMetricDetailOperatorContext,
  businessDate: string,
): ProductCostBusinessMetricDetailQueryKey {
  if (!isCanonicalDate(businessDate) || !detailOperatorContextIsCanonical(currentOperatorContext)) {
    throw productCostBusinessMetricDetailRequestInvalid();
  }
  return [
    "product-cost-business-metric-detail",
    currentOperatorContext.tenantId,
    currentOperatorContext.storeId,
    currentOperatorContext.operatorId,
    businessDate,
  ];
}

export async function getProductCostBusinessMetricDetail(
  businessDate: string,
  currentOperatorContext: ProductCostBusinessMetricDetailOperatorContext,
  signal?: AbortSignal,
): Promise<ProductCostBusinessMetricDetailReadModel> {
  if (!isCanonicalDate(businessDate) || !detailOperatorContextIsCanonical(currentOperatorContext)) {
    throw productCostBusinessMetricDetailRequestInvalid();
  }
  const query = new URLSearchParams({ businessDate });
  const value = await requestOperatorJson<unknown>(
    `/v1/owner/business-metrics/product-cost-details?${query.toString()}`,
    {
      cache: "no-store",
      ...(signal ? { signal } : {}),
    },
  );
  if (
    !isProductCostBusinessMetricDetailReadModel(value, {
      tenantId: currentOperatorContext.tenantId,
      storeId: currentOperatorContext.storeId,
      businessDate,
    })
  ) {
    throw new ApiRequestError(
      "GATEWAY_RESPONSE_INVALID",
      "商品成本明细无法安全确认，页面已隐藏旧明细和金额，请重新读取",
    );
  }
  return value;
}

function commissionPayableBusinessMetricDetailRequestInvalid(): ApiRequestError {
  return new ApiRequestError(
    "REQUEST_VALIDATION_FAILED",
    "提成应付明细的营业日期或当前操作身份不正确，系统没有读取明细",
    400,
  );
}

export function commissionPayableBusinessMetricDetailQueryKey(
  currentOperatorContext: CommissionPayableBusinessMetricDetailOperatorContext,
  businessDate: string,
): CommissionPayableBusinessMetricDetailQueryKey {
  if (!isCanonicalDate(businessDate) || !detailOperatorContextIsCanonical(currentOperatorContext)) {
    throw commissionPayableBusinessMetricDetailRequestInvalid();
  }
  return [
    "commission-payable-business-metric-detail",
    currentOperatorContext.tenantId,
    currentOperatorContext.storeId,
    currentOperatorContext.operatorId,
    businessDate,
  ];
}

export async function getCommissionPayableBusinessMetricDetail(
  businessDate: string,
  currentOperatorContext: CommissionPayableBusinessMetricDetailOperatorContext,
  signal?: AbortSignal,
): Promise<CommissionPayableBusinessMetricDetailReadModel> {
  if (!isCanonicalDate(businessDate) || !detailOperatorContextIsCanonical(currentOperatorContext)) {
    throw commissionPayableBusinessMetricDetailRequestInvalid();
  }
  const query = new URLSearchParams({ businessDate });
  const value = await requestOperatorJson<unknown>(
    `/v1/owner/business-metrics/commission-payable-details?${query.toString()}`,
    {
      cache: "no-store",
      ...(signal ? { signal } : {}),
    },
  );
  if (
    !isCommissionPayableBusinessMetricDetailReadModel(value, {
      tenantId: currentOperatorContext.tenantId,
      storeId: currentOperatorContext.storeId,
      businessDate,
    })
  ) {
    throw new ApiRequestError(
      "GATEWAY_RESPONSE_INVALID",
      "提成应付明细无法安全确认，页面已隐藏旧明细和金额，请重新读取",
    );
  }
  return value;
}

function cashDifferenceBusinessMetricDetailRequestInvalid(): ApiRequestError {
  return new ApiRequestError(
    "REQUEST_VALIDATION_FAILED",
    "现金差异明细的营业日期或当前操作身份不正确，系统没有读取明细",
    400,
  );
}

export function cashDifferenceBusinessMetricDetailQueryKey(
  currentOperatorContext: CashDifferenceBusinessMetricDetailOperatorContext,
  businessDate: string,
): CashDifferenceBusinessMetricDetailQueryKey {
  if (!isCanonicalDate(businessDate) || !detailOperatorContextIsCanonical(currentOperatorContext)) {
    throw cashDifferenceBusinessMetricDetailRequestInvalid();
  }
  return [
    "cash-difference-business-metric-detail",
    currentOperatorContext.tenantId,
    currentOperatorContext.storeId,
    currentOperatorContext.operatorId,
    businessDate,
  ];
}

export async function getCashDifferenceBusinessMetricDetail(
  businessDate: string,
  currentOperatorContext: CashDifferenceBusinessMetricDetailOperatorContext,
  signal?: AbortSignal,
): Promise<CashDifferenceBusinessMetricDetailReadModel> {
  if (!isCanonicalDate(businessDate) || !detailOperatorContextIsCanonical(currentOperatorContext)) {
    throw cashDifferenceBusinessMetricDetailRequestInvalid();
  }
  const query = new URLSearchParams({ businessDate });
  const value = await requestOperatorJson<unknown>(
    `/v1/owner/business-metrics/cash-difference-details?${query.toString()}`,
    {
      cache: "no-store",
      ...(signal ? { signal } : {}),
    },
  );
  if (
    !isCashDifferenceBusinessMetricDetailReadModel(value, {
      tenantId: currentOperatorContext.tenantId,
      storeId: currentOperatorContext.storeId,
      businessDate,
    })
  ) {
    throw new ApiRequestError(
      "GATEWAY_RESPONSE_INVALID",
      "现金差异明细无法安全确认，页面已隐藏旧明细和金额，请重新读取",
    );
  }
  return value;
}

export async function getBusinessMetricDictionary(
  businessDate: string,
  currentOperatorContext: BusinessMetricCurrentOperatorContext,
  signal?: AbortSignal,
): Promise<BusinessMetricDictionaryReadModel> {
  if (!isCanonicalDate(businessDate) || !operatorContextIsCanonical(currentOperatorContext)) {
    throw new ApiRequestError(
      "REQUEST_VALIDATION_FAILED",
      "营业日期或当前门店身份不正确，系统没有读取经营指标",
      400,
    );
  }
  const query = new URLSearchParams({ businessDate });
  const value = await requestOperatorJson<unknown>(
    `/v1/owner/business-metrics?${query.toString()}`,
    {
      cache: "no-store",
      ...(signal ? { signal } : {}),
    },
  );
  if (
    !isBusinessMetricDictionaryReadModel(value, {
      tenantId: currentOperatorContext.tenantId,
      storeId: currentOperatorContext.storeId,
      businessDate,
    })
  ) {
    throw new ApiRequestError(
      "GATEWAY_RESPONSE_INVALID",
      "经营指标无法安全确认，页面已隐藏旧指标和金额，请重新读取",
    );
  }
  return value;
}

function reportResponseInvalid(): ApiRequestError {
  return new ApiRequestError(
    "GATEWAY_RESPONSE_INVALID",
    "经营指标报表无法安全确认，旧报表和金额已隐藏，请重新读取",
  );
}

async function sha256BytesHex(value: Uint8Array): Promise<string> {
  if (!globalThis.crypto?.subtle || typeof globalThis.crypto.subtle.digest !== "function") {
    throw new ApiRequestError(
      "BUSINESS_METRIC_REPORT_DIGEST_UNAVAILABLE",
      "当前设备无法安全核对报表内容，报表没有下载，请更换受支持的设备后重试",
    );
  }
  let digest: ArrayBuffer;
  try {
    const digestInput = new ArrayBuffer(value.byteLength);
    new Uint8Array(digestInput).set(value);
    digest = await globalThis.crypto.subtle.digest("SHA-256", digestInput);
  } catch {
    throw new ApiRequestError(
      "BUSINESS_METRIC_REPORT_DIGEST_UNAVAILABLE",
      "当前设备无法安全核对报表内容，报表没有下载，请更换受支持的设备后重试",
    );
  }
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function sha256Hex(value: string): Promise<string> {
  return sha256BytesHex(new TextEncoder().encode(value));
}

const BASE64_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
const CANONICAL_BASE64 = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/u;

function encodeBase64(bytes: Uint8Array): string {
  let encoded = "";
  for (let offset = 0; offset < bytes.length; offset += 3) {
    const first = bytes[offset];
    if (first === undefined) return encoded;
    const second = bytes[offset + 1];
    const third = bytes[offset + 2];
    const block = (first << 16) | ((second ?? 0) << 8) | (third ?? 0);
    encoded += BASE64_ALPHABET[(block >>> 18) & 63];
    encoded += BASE64_ALPHABET[(block >>> 12) & 63];
    encoded += second === undefined ? "=" : BASE64_ALPHABET[(block >>> 6) & 63];
    encoded += third === undefined ? "=" : BASE64_ALPHABET[block & 63];
  }
  return encoded;
}

function decodeCanonicalBase64(value: string): Uint8Array<ArrayBuffer> | null {
  if (value.length === 0 || value.length % 4 !== 0 || !CANONICAL_BASE64.test(value)) {
    return null;
  }
  const padding = value.endsWith("==") ? 2 : value.endsWith("=") ? 1 : 0;
  const bytes = new Uint8Array((value.length / 4) * 3 - padding);
  let byteOffset = 0;
  for (let offset = 0; offset < value.length; offset += 4) {
    const first = BASE64_ALPHABET.indexOf(value[offset] ?? "");
    const second = BASE64_ALPHABET.indexOf(value[offset + 1] ?? "");
    const thirdCharacter = value[offset + 2];
    const fourthCharacter = value[offset + 3];
    const third = thirdCharacter === "=" ? 0 : BASE64_ALPHABET.indexOf(thirdCharacter ?? "");
    const fourth = fourthCharacter === "=" ? 0 : BASE64_ALPHABET.indexOf(fourthCharacter ?? "");
    if (first < 0 || second < 0 || third < 0 || fourth < 0) return null;
    const block = (first << 18) | (second << 12) | (third << 6) | fourth;
    if (byteOffset < bytes.length) bytes[byteOffset++] = (block >>> 16) & 255;
    if (byteOffset < bytes.length) bytes[byteOffset++] = (block >>> 8) & 255;
    if (byteOffset < bytes.length) bytes[byteOffset++] = block & 255;
  }
  return encodeBase64(bytes) === value ? bytes : null;
}

export function businessMetricReportXlsxBytes(
  report: BusinessMetricReportBundleReadModel,
): Uint8Array<ArrayBuffer> {
  const bytes = decodeCanonicalBase64(report.xlsxBase64);
  if (!bytes) throw reportResponseInvalid();
  return bytes;
}

export async function getBusinessMetricReport(
  businessDate: string,
  currentOperatorContext: BusinessMetricCurrentOperatorContext,
  signal?: AbortSignal,
): Promise<BusinessMetricReportReadModel> {
  if (!isCanonicalDate(businessDate) || !operatorContextIsCanonical(currentOperatorContext)) {
    throw new ApiRequestError(
      "REQUEST_VALIDATION_FAILED",
      "营业日期或当前门店身份不正确，系统没有读取经营指标报表",
      400,
    );
  }
  const query = new URLSearchParams({ businessDate });
  const value = await requestOperatorJson<unknown>(
    `/v1/owner/business-metrics/report?${query.toString()}`,
    {
      cache: "no-store",
      ...(signal ? { signal } : {}),
    },
  );
  if (
    !isBusinessMetricReportReadModel(value, {
      tenantId: currentOperatorContext.tenantId,
      storeId: currentOperatorContext.storeId,
      businessDate,
    })
  ) {
    throw reportResponseInvalid();
  }
  if ((await sha256Hex(value.csv)) !== value.contentSha256) {
    throw reportResponseInvalid();
  }
  return value;
}

export async function getBusinessMetricReportBundle(
  businessDate: string,
  currentOperatorContext: BusinessMetricCurrentOperatorContext,
  signal?: AbortSignal,
): Promise<BusinessMetricReportBundleReadModel> {
  if (!isCanonicalDate(businessDate) || !operatorContextIsCanonical(currentOperatorContext)) {
    throw new ApiRequestError(
      "REQUEST_VALIDATION_FAILED",
      "营业日期或当前门店身份不正确，系统没有读取经营指标报表文件",
      400,
    );
  }
  const query = new URLSearchParams({ businessDate });
  const value = await requestOperatorJson<unknown>(
    `/v1/owner/business-metrics/report-bundle?${query.toString()}`,
    {
      cache: "no-store",
      ...(signal ? { signal } : {}),
    },
  );
  if (
    !isBusinessMetricReportBundleReadModel(value, {
      tenantId: currentOperatorContext.tenantId,
      storeId: currentOperatorContext.storeId,
      businessDate,
    })
  ) {
    throw reportResponseInvalid();
  }
  const xlsxBytes = businessMetricReportXlsxBytes(value);
  const csvSha256 = await sha256Hex(value.csv);
  const xlsxSha256 = await sha256BytesHex(xlsxBytes);
  if (csvSha256 !== value.csvSha256 || xlsxSha256 !== value.xlsxSha256) {
    throw reportResponseInvalid();
  }
  return value;
}

export function businessMetricDrillHref(
  drillTarget: BusinessMetricReadModel["drillTarget"],
  businessDate: string,
  metricId?: BusinessMetricReadModel["id"],
  metricAvailability?: BusinessMetricReadModel["availability"],
): string | null {
  if (!isCanonicalDate(businessDate)) {
    throw new ApiRequestError(
      "BUSINESS_METRIC_DRILL_DATE_INVALID",
      "指标营业日期无法安全确认，明细入口没有打开",
    );
  }
  const href = drillHrefByTarget[drillTarget];
  if (!href) {
    throw new ApiRequestError(
      "BUSINESS_METRIC_DRILL_TARGET_INVALID",
      "这项指标的明细入口无法安全打开，请从业务菜单进入",
    );
  }
  if (metricId === "REFUNDS") {
    if (drillTarget !== "RECONCILIATION") {
      throw new ApiRequestError(
        "BUSINESS_METRIC_DRILL_TARGET_INVALID",
        "退款指标的明细入口无法安全打开，请从订单菜单进入",
      );
    }
    return `${href}?${new URLSearchParams({
      view: "REFUND_DETAILS",
      businessDate,
    }).toString()}`;
  }
  if (metricId === "CONFIRMED_RECEIPTS") {
    if (drillTarget !== "RECONCILIATION") {
      throw new ApiRequestError(
        "BUSINESS_METRIC_DRILL_TARGET_INVALID",
        "已确认实收指标的明细入口无法安全打开，请从订单菜单进入",
      );
    }
    return `${href}?${new URLSearchParams({
      view: "CONFIRMED_RECEIPT_DETAILS",
      businessDate,
    }).toString()}`;
  }
  if (metricId === "PRODUCT_COST") {
    if (drillTarget !== "ORDERS") {
      throw new ApiRequestError(
        "BUSINESS_METRIC_DRILL_TARGET_INVALID",
        "商品成本指标的明细入口无法安全打开，请从订单菜单进入",
      );
    }
    if (metricAvailability !== "AVAILABLE" && metricAvailability !== "PARTIAL") return null;
    return `${href}?${new URLSearchParams({
      view: "PRODUCT_COST_DETAILS",
      businessDate,
    }).toString()}`;
  }
  if (metricId === "COMMISSION_PAYABLE") {
    if (drillTarget !== "COACH_SERVICES") {
      throw new ApiRequestError(
        "BUSINESS_METRIC_DRILL_TARGET_INVALID",
        "提成应付指标的明细入口无法安全打开，请从订单菜单进入",
      );
    }
    if (metricAvailability !== "AVAILABLE" && metricAvailability !== "PARTIAL") return null;
    return `/orders?${new URLSearchParams({
      view: "COMMISSION_PAYABLE_DETAILS",
      businessDate,
    }).toString()}`;
  }
  if (metricId === "CASH_DIFFERENCE") {
    if (drillTarget !== "CASH_SHIFTS") {
      throw new ApiRequestError(
        "BUSINESS_METRIC_DRILL_TARGET_INVALID",
        "现金差异指标的明细入口无法安全打开，请从交班菜单进入",
      );
    }
    if (metricAvailability !== "AVAILABLE" && metricAvailability !== "PARTIAL") return null;
    return `${href}?${new URLSearchParams({
      view: "CASH_DIFFERENCE_DETAILS",
      businessDate,
    }).toString()}`;
  }
  if (metricId === "ORDER_RECEIVABLE") {
    if (drillTarget !== "ORDERS") {
      throw new ApiRequestError(
        "BUSINESS_METRIC_DRILL_TARGET_INVALID",
        "订单应收指标的明细入口无法安全打开，请从订单菜单进入",
      );
    }
    if (metricAvailability !== "AVAILABLE" && metricAvailability !== "PARTIAL") return null;
    return `${href}?${new URLSearchParams({
      view: "ORDER_RECEIVABLE_DETAILS",
      businessDate,
    }).toString()}`;
  }
  if (drillTarget === "ORDERS" || drillTarget === "RECONCILIATION") {
    return `${href}?${new URLSearchParams({ businessDate }).toString()}`;
  }
  if (drillTarget === "RECEIVABLES") {
    return `${href}?${new URLSearchParams({
      view: "MANUAL",
      category: "MEMBER_RECEIVABLE",
      businessDate,
    }).toString()}`;
  }
  if (drillTarget === "MEMBER_FUNDS" && metricId !== undefined) {
    if (!memberFundMetricIdIsCanonical(metricId)) {
      throw new ApiRequestError(
        "BUSINESS_METRIC_DRILL_TARGET_INVALID",
        "这项会员资金指标的明细入口无法安全打开，请从会员菜单进入",
      );
    }
    return `${href}?${new URLSearchParams({
      view: "FUND_DETAILS",
      metricId,
      businessDate,
    }).toString()}`;
  }
  return href;
}

export function businessMetricDisplay(metric: BusinessMetricReadModel): BusinessMetricDisplayModel {
  if (metric.availability === "NOT_AVAILABLE" || metric.value === null) {
    return {
      availabilityLabel: "暂不可用",
      valueLabel: "暂不可用",
      reason: metric.reason,
      attention: true,
    };
  }
  const valueLabel = isMemberBalanceMovementMetric(metric)
    ? formatMemberBalanceMovement(metric.value)
    : metric.unit === "CNY_FEN"
      ? formatFen(metric.value)
      : `${metric.value} 项`;
  if (metric.availability === "PARTIAL") {
    return {
      availabilityLabel: "部分可用",
      valueLabel,
      reason: metric.reason,
      attention: true,
    };
  }
  return {
    availabilityLabel: "数据完整",
    valueLabel,
    reason: null,
    attention: false,
  };
}
